import IntegrationCard from "./IntegrationCard";
import { getFilePath, REACT_APP_ADMIN_LINK } from "../../utils";

export type IntegrationInfoType = {
  type:
    | "canvas"
    | "schoology"
    | "infinite_campus"
    | "google_classroom"
    | "clever"
    | "classlink";
  title: string;
  logoUrl: string;
  description: React.ReactNode;
  installLink?: string;
  helpCenterLink?: string;
  integrationGuideLink?: string;
  helpCenterLinks?: {
    title: string;
    url: string;
  }[];
  sampleIssuer?: string;
};

export const integrationInfo: IntegrationInfoType[] = [
  {
    type: "canvas",
    title: "Canvas",
    logoUrl: getFilePath("/images/integrations/canvas.png"),
    description:
      "Our Canvas integration includes rostering and grade passback.",
    installLink: `${REACT_APP_ADMIN_LINK}/integrations/details?type=canvas`,
    sampleIssuer: "https://canvas.instructure.com",
    integrationGuideLink:
      "https://files.deltamath.com/DeltaMathConfigGuide-Canvas-SiteLicense.pdf",
    helpCenterLinks: [
      {
        title: "Create Classes and Roster Students",
        url: "https://help.deltamath.com/en_US/getting-started-c/create-classes-and-roster-students-canvas",
      },
      {
        title: "Class Settings",
        url: "https://help.deltamath.com/en_US/getting-started-c/class-settings-canvas",
      },
      {
        title: "External Tool Vs. Sidebar",
        url: "https://help.deltamath.com/en_US/assignments-c/external-tool-vs-sidebar-canvas",
      },
      {
        title: "Cross-listed Classes",
        url: "https://help.deltamath.com/en_US/getting-started-c/crosslisted-classes",
      },
      {
        title: "All About Assignments",
        url: "https://help.deltamath.com/en_US/assignments-c",
      },
    ],
  },
  {
    type: "schoology",
    title: "Schoology",
    logoUrl: getFilePath("/images/integrations/schoology.png"),
    description:
      "Our Schoology integration includes rostering and grade passback.",
    installLink: `${REACT_APP_ADMIN_LINK}/integrations/details?type=schoology`,
    sampleIssuer: "https://lti-service.svc.schoology.com",
    integrationGuideLink:
      "https://files.deltamath.com/DeltaMathConfigGuide-Schoology-SiteLicense.pdf",
    helpCenterLinks: [
      {
        title: "Create Classes and Roster Students",
        url: "https://help.deltamath.com/en_US/getting-started-s/create-classes-and-roster-students-schoology",
      },
      {
        title: "Class Settings",
        url: "https://help.deltamath.com/en_US/getting-started-s/class-settings-schoology",
      },
      {
        title: "External Tool Vs. Sidebar",
        url: "https://help.deltamath.com/en_US/assignments-s/external-tool-vs-sidebar-schoology",
      },
      {
        title: "All About Assignments",
        url: "https://help.deltamath.com/en_US/assignments-s",
      },
    ],
  },
  // {
  //   type: "infinite_campus",
  //   title: "Infinite Campus",
  //   logoUrl: getFilePath("/images/integrations/infinite_campus.png"),
  //   description:
  //     "Our Infinite Campus integration includes rostering and grade passback.",
  //   installLink: `${REACT_APP_ADMIN_LINK}/integrations/details?type=infinite_campus`,
  //   sampleIssuer: "https://infinitecampus.org",
  // },
  {
    type: "infinite_campus",
    title: "",
    logoUrl: "",
    description: "",
    installLink: ``,
    sampleIssuer: "",
  },
  {
    type: "google_classroom",
    title: "Google Classroom",
    logoUrl: getFilePath("/images/integrations/google_classroom.png"),
    description: (
      <div className="flex flex-col gap-8">
        <span>
          Our Google Classroom integration includes rostering and grade
          passback.
        </span>
        <span>
          Teachers can{" "}
          <a
            href="https://help.deltamath.com/en_US/getting-started-gc/set-up-gc-integration"
            target="_blank"
            rel="noreferrer"
            className="text-blue-500 underline"
          >
            set up the Google Classroom integration
          </a>{" "}
          within their accounts.
        </span>
      </div>
    ),
    helpCenterLink: "https://help.deltamath.com/en_US/google-classroom",
  },
  {
    type: "clever",
    title: "Clever",
    logoUrl: getFilePath("/images/integrations/clever.png"),
    description: (
      <div className="flex flex-col gap-8">
        <span>
          Our Clever integration includes automatic rostering with a nightly
          data sync.
        </span>
        <span>
          To set up the Clever integration, please reach out to us at{" "}
          <a
            href="mailto:support@deltamath.com"
            className="text-blue-500 underline"
          >
            support@deltamath.com
          </a>
        </span>
      </div>
    ),
    helpCenterLink: "https://help.deltamath.com/en_US/clever",
  },
  {
    type: "classlink",
    title: "ClassLink",
    logoUrl: getFilePath("/images/integrations/classlink.png"),
    description: (
      <div className="flex flex-col gap-8">
        <span>
          Our ClassLink integration includes automatic rostering with a nightly
          data sync.
        </span>
        <span>
          To set up the ClassLink integration, please reach out to us at{" "}
          <a
            href="mailto:support@deltamath.com"
            className="text-blue-500 underline"
          >
            support@deltamath.com
          </a>
        </span>
      </div>
    ),
    helpCenterLink: "https://help.deltamath.com/en_US/classlink",
  },
];

const IntegrationLanding = () => {
  return (
    <>
      <div className="bg-white p-6">
        <h3 className="mb-2 font-serif text-2xl font-bold leading-10">
          Integrations
        </h3>
        <p className="flex flex-col gap-2 text-gray-500">
          <span>
            Please refer to the information below for guidance on setting up an
            integration for your site license.
          </span>
          <span>
            <b>Note:</b> While we have the ability to enable multiple
            integrations for your site, we recommend using a single integration
            to ensure a more streamlined experience for teachers and students.
          </span>
        </p>
      </div>
      <div className="grid grid-cols-3 gap-4 p-6">
        {integrationInfo.map((integration, i) => (
          <IntegrationCard key={`${integration.title}-${i}`} {...integration} />
        ))}
      </div>
    </>
  );
};

export default IntegrationLanding;
